import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { updateInvoice } from '@/api/invoices';
import { qk } from '@/lib/query';
import { formatDate } from '@/lib/format';
import type { InvoiceDetail } from '@/types';
import { Group } from '@m/ui/List';
import { BarButton } from '@m/ui/Controls';
import { Sheet } from '@m/ui/Sheet';

/** A draft's dates and notes. Line items stay a desktop job. */
export function EditInvoiceSheet({ invoice, open, onClose }: { invoice: InvoiceDetail; open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [issueDate, setIssueDate] = useState(invoice.issueDate ?? '');
  const [dueDate, setDueDate] = useState(invoice.dueDate ?? '');
  const [notes, setNotes] = useState(invoice.notes ?? '');
  const [saving, setSaving] = useState(false);
  const dirty = issueDate !== (invoice.issueDate ?? '') || dueDate !== (invoice.dueDate ?? '') || notes !== (invoice.notes ?? '');

  const save = async () => {
    setSaving(true);
    try {
      await updateInvoice(invoice.id, { issueDate: issueDate || undefined, dueDate: dueDate || undefined, notes: notes || null });
      await Promise.all([
        qc.invalidateQueries({ queryKey: qk.invoice(invoice.id) }),
        qc.invalidateQueries({ queryKey: qk.invoicesRoot })
      ]);
      toast.success('Invoice saved');
      onClose();
    } catch {
      toast.error('Could not save the invoice.');
    } finally {
      setSaving(false);
    }
  };

  const dateRow = (label: string, value: string, set: (v: string) => void) => (
    <label className="relative flex min-h-[52px] items-center gap-3 px-4">
      <span className="flex-1">{label}</span>
      <span className="flex h-[34px] items-center rounded-lg bg-[var(--page)] px-3">{value ? formatDate(value) : 'None'}</span>
      <input type="date" value={value} onChange={(e) => set(e.target.value)} className="absolute inset-0 opacity-0" aria-label={label} />
    </label>
  );

  return (
    <Sheet
      open={open}
      onClose={onClose}
      dirty={dirty}
      full
      title="Edit invoice"
      subtitle={invoice.invoiceNumber}
      leading={<BarButton onClick={onClose}>Cancel</BarButton>}
      trailing={
        <BarButton className="font-semibold" disabled={!dirty || saving} onClick={save}>
          Save
        </BarButton>
      }
    >
      <div className="flex flex-col gap-5">
        <Group>
          {dateRow('Issued', issueDate, setIssueDate)}
          {dateRow('Due', dueDate, setDueDate)}
        </Group>
        <label className="flex flex-col gap-1.5 rounded-2xl bg-[var(--surface)] px-4 py-3">
          <span className="text-caption font-medium text-[var(--text-muted)]">Notes</span>
          <textarea
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Shown on the invoice"
            className="resize-none bg-transparent text-body leading-6 outline-none placeholder:text-[var(--text-subtle)]"
          />
        </label>
        <p className="px-4 text-caption text-[var(--text-muted)]">To change line items, open this invoice on billable.to.</p>
      </div>
    </Sheet>
  );
}
